import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useDirectorResetPasswordMutation } from '../../redux/services/director-api';

const Root = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: #f4f1e9;
  font-family: 'Avenir Next', 'Trebuchet MS', sans-serif;
`;

const Card = styled.div`
  width: min(100%, 440px);
  padding: 48px 40px;
  background: #ffffff;
  border: 1px solid rgba(53, 62, 53, 0.45);
  border-radius: 4px;
  box-shadow: 0 28px 80px rgba(38, 42, 33, 0.14);

  @media (max-width: 480px) { padding: 32px 24px; }
`;

const Title = styled.h1`
  margin: 0 0 8px;
  color: #284B32;
  font-size: 1.6rem;
  font-weight: 700;
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0 0 32px;
  color: #8d8d8d;
  font-size: 0.9rem;
  text-align: center;
  line-height: 1.5;
`;

const Alert = styled.div`
  margin-bottom: 18px;
  padding: 12px 14px;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
  border: 1px solid ${p => p.success ? '#c1efd0' : '#efc3c3'};
  color: ${p => p.success ? '#1e5c30' : '#9d2f2f'};
  background: ${p => p.success ? '#f0fbf4' : '#fff3f3'};
`;

const Field = styled.label`
  position: relative;
  display: block;
  margin-bottom: 22px;
`;

const FieldLabel = styled.span`
  position: absolute;
  top: -10px; left: 16px;
  padding: 0 7px;
  color: #8b8b8b;
  background: #ffffff;
  font-size: 0.9rem;
  line-height: 1;
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  height: 56px;
  padding: 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  color: #1f1f1f;
  font-size: 1rem;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &:focus {
    border-color: #284b31;
    box-shadow: 0 0 0 3px rgba(40,75,49,0.12);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 56px;
  border: 0;
  border-radius: 4px;
  background: #284b31;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  &:hover  { background: #203c27; }
  &:active { transform: translateY(1px); }
  &:disabled { background: #9ab09f; cursor: not-allowed; }
`;

const BackLink = styled.a`
  display: block;
  margin-top: 20px;
  text-align: center;
  font-size: 0.875rem;
  color: #284B32;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const DirectorResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [resetPassword, { isLoading }] = useDirectorResetPasswordMutation();

  const [form, setForm]       = useState({ password: '', confirm: '' });
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <Root>
        <Card>
          <Title>Enlace inválido</Title>
          <Subtitle>
            Este enlace no contiene un token de recuperación. Solicita un nuevo enlace desde la página de login.
          </Subtitle>
          <Alert>Token no encontrado en la URL</Alert>
          <BackLink href="/director/login">Volver al login</BackLink>
        </Card>
      </Root>
    );
  }

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (form.password !== form.confirm) {
      setError('Las contraseñas no coinciden');
      return;
    }

    try {
      await resetPassword({ token, password: form.password }).unwrap();
      setSuccess(true);
      setTimeout(() => navigate('/director/login'), 2000);
    } catch (err) {
      const msg = err?.data?.message || 'Error al restablecer la contraseña';
      setError(msg);
    }
  };

  return (
    <Root>
      <Card>
        <Title>Nueva contraseña</Title>
        <Subtitle>
          Elige una contraseña segura para tu cuenta de director.
        </Subtitle>

        {error   && <Alert>{error}</Alert>}
        {success && <Alert success>Contraseña restablecida. Redirigiendo al login...</Alert>}

        <form onSubmit={handleSubmit}>
          <Field>
            <FieldLabel>Nueva contraseña</FieldLabel>
            <Input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              minLength={6}
              required
              disabled={success}
            />
          </Field>

          <Field>
            <FieldLabel>Confirmar contraseña</FieldLabel>
            <Input
              type="password"
              name="confirm"
              value={form.confirm}
              onChange={handleChange}
              autoComplete="new-password"
              minLength={6}
              required
              disabled={success}
            />
          </Field>

          <SubmitButton type="submit" disabled={isLoading || success}>
            {isLoading ? 'Guardando...' : 'Restablecer contraseña'}
          </SubmitButton>
        </form>

        <BackLink href="/director/login">Volver al login</BackLink>
      </Card>
    </Root>
  );
};

export default DirectorResetPassword;
